/* UniversalApplicationShell · Constitutional Refactor · Batch C2a
 * ================================================================
 * The universal layout that hosts every non-Work-Mode surface.
 *
 * Renders:
 *   • the routed page content in the content slot
 *   • the docked ConversationBar as the platform's front door,
 *     EXCEPT on Work Mode routes (Work Mode Exception).
 *
 * Constitutionally aligned:
 *   • Article 7 · Reduce Cognitive Load — one entry point, always.
 *   • Article 9 · Consistency Builds Trust — same surface everywhere.
 *   • Conversation-First doctrine — bar reachable from every surface.
 *   • Work Mode Exception — microphone only while teaching.
 *
 * C2a scope: pure shell. No domain refactor, no card removal,
 * no simplification. Existing pages render exactly as before,
 * now hosted inside the shell.
 */
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { UniversalContextBar } from "./UniversalContextBar";
import { AttentionInbox } from "./AttentionInbox";
import { ConstitutionalToolbox } from "./ConstitutionalToolbox";
import FounderCockpit from "./founder_cockpit/FounderCockpit";

// Work Mode Exception · task-first, evidence-first, teaching-first.
// Any pathname that starts with any of these gets the voice-only bar.
const WorkModeVoiceControl = UniversalContextBar;

const WORK_MODE_PREFIXES = [
  "/work-mode",
  "/instructor/work-mode",
];

function isWorkModeRoute(pathname) {
  if (typeof pathname !== "string") return false;
  return WORK_MODE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// Public helper — imported by doctrine tests and future audit code.
// Kept as a named export so the doctrine can lock the prefix list.
export function isWorkModePath(pathname) {
  return isWorkModeRoute(pathname);
}

export function UniversalApplicationShell({ children, sessionControls }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  const workMode = isWorkModeRoute(location?.pathname);
  const isolatedPart3 = /^\/(?:instructor\/)?work-mode\/?$/i.test(location?.pathname || "")
    && Boolean(new URLSearchParams(location?.search || "").get("part3_session"));
  // Public and authentication routes render only their page content.
  // Operational controls must not mount until the session is confirmed.
  const showOperationalChrome = Boolean(user) && !loading && !workMode;
  const accountScope = [user?.id || user?._id, user?.operating_mode, user?.admin_view_role, user?.founder_mode_subject_user_id].join(":");

  return (
    <div
      data-testid="universal-application-shell"
      className="flex flex-col bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100"
      style={workMode || showOperationalChrome ? { height: "100dvh", overflow: "hidden" } : { minHeight: "100vh" }}
    >
      {sessionControls && !workMode && (
        <div data-testid="universal-session-controls" className="shrink-0">
          {sessionControls}
        </div>
      )}
      {showOperationalChrome && (
        <header data-testid="universal-app-header" className="shrink-0 relative z-40 flex items-start gap-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-2 pt-[env(safe-area-inset-top,0px)]">
          <div data-testid="universal-toolbox-mount" className="shrink-0"><ConstitutionalToolbox /></div>
          <div data-testid="universal-attention-mount" className="min-w-0 flex-1">
            <AttentionInbox key={accountScope} />
          </div>
        </header>
      )}
      <main
        key={accountScope}
        data-testid="universal-shell-content"
        className={`flex-1 flex flex-col ${workMode || showOperationalChrome ? "min-h-0 overflow-y-auto overscroll-contain" : ""}`}
      >
        {children}
      </main>
      {showOperationalChrome && (
        <div
          data-testid="universal-conversation-bar-mount"
          className="shrink-0 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-3 py-2"
        >
          <UniversalContextBar />
        </div>
      )}
      {Boolean(user) && !loading && workMode && !isolatedPart3 && (
        <div
          data-testid="work-mode-voice-control-mount"
          className="shrink-0 border-t border-slate-800 bg-slate-950 px-3 pt-2 pb-safe-4 flex justify-center"
        >
          <WorkModeVoiceControl voiceOnly />
        </div>
      )}
      {/* GC61 · Canonical docked mobile primary navigation.
          Docked in-flow — occupies its own layout space, edge-to-edge,
          no floating pill, no shadow. Work Mode is exempt (task-first,
          teaching-first) so instructors get a full-screen surface. */}
      {showOperationalChrome && (
        <div data-testid="universal-cockpit-mount" className="w-full shrink-0">
          <FounderCockpit />
        </div>
      )}
    </div>
  );
}

export default UniversalApplicationShell;


